import { UserApi } from "./user.api";
import { ProfileApi } from "./profile.api";
import { DiscountApi } from "./discount.api";
import { ReservationApi } from "./reservation.api";
import { PickupPointApi } from "./pickup-point.api";
import axios from "axios";
import i18n from "../i18n";
import posthog from "posthog-js";
import { isDevMode } from "@/utils/is-devmode";
import { useToast } from "@/utils/use-toast";
import { getApiValidationErrors } from "@/utils/get-api-validation-errors";
import { StocksApi } from "./stocks.api";
import { GroceryApi } from "./grocery.api";
import { WoltDeliveryApi } from "./wolt-delivery.api";
import { VenipakDeliveryApi } from "./venipak-delivery.api";
import { ManguMangasDeliveryApi } from "./mangu-mangas-delivery.api";
import { SendPackDeliveryApi } from "./sendpack-delivery.api";
import { FoodPickupPointApi } from "./food-pickup-point.api";
import { PartnerPickupApi } from "./partner-pickup.api";
import { BlogApi } from "./blog.api";
import { OrganizationsApi } from "./organizations.api";

const axionsInstance = axios.create({
  headers: {
    "X-Requested-With": "XMLHttpRequest", // help laravel identify ajax requests
  },
});

const ignoreToastsOn = ["/login"];

axionsInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      !axios.isCancel(error) && // skip request cancel errors
      !ignoreToastsOn.includes(error.config.url) && // skip ignored routes
      !getApiValidationErrors(error) // handle validation errors manually
    ) {
      displayErrorToast(error);
    }

    return Promise.reject(error);
  }
);

axionsInstance.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  const distinctId = posthog.get_distinct_id();
  if (distinctId) {
    config.headers["X-Posthog-Distinct-Id"] = distinctId;
  }
  return config;
});

const displayErrorToast = (error: any): void => {
  if (isDevMode) {
    console.error(error.response);
  }

  useToast().error(getErrorMessage(error.response));
};

const getErrorMessage = (errorResponse: {
  status: number;
  statusText: string;
  data?: { code?: string };
}): string => {
  const { t } = i18n.global;
  const errorKey =
    errorResponse.data?.code || errorResponse?.status || "default";
  const key = `apiError.${errorKey}`;
  const message = t(key, errorResponse);

  return message === key ? t("apiError.default", errorResponse) : message;
};

export const api = {
  pickupPoint: new PickupPointApi(axionsInstance),
  stocks: new StocksApi(axionsInstance),
  reservation: new ReservationApi(axionsInstance),
  grocery: new GroceryApi(axionsInstance),
  user: new UserApi(axionsInstance),
  profile: new ProfileApi(axionsInstance),
  discount: new DiscountApi(axionsInstance),
  woltDelivery: new WoltDeliveryApi(axionsInstance),
  venipakDelivery: new VenipakDeliveryApi(axionsInstance),
  manguMangasDelivery: new ManguMangasDeliveryApi(axionsInstance),
  sendPackDelivery: new SendPackDeliveryApi(axionsInstance),
  foodPickupPoint: new FoodPickupPointApi(axionsInstance),
  partnerPickup: new PartnerPickupApi(axionsInstance),
  blog: new BlogApi(axionsInstance),
  organizations: new OrganizationsApi(axionsInstance),
};
